import * as React from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";

import { cn } from "../../lib/utils";

type SwitchProps = React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>;

/**
 * A boolean that takes effect the moment it moves.
 *
 * Use it for a view mode or a setting — "only rows with a problem", "dark
 * theme" — where the change applies immediately and the control is the state.
 * Use `Checkbox` for a selection inside a form that a submit will act on later.
 * They are both booleans; the difference a reader feels is whether anything has
 * happened yet.
 *
 * Label it by what turning it ON does, never by the state it is in. "Only rows
 * with a problem" is honest in both positions; "Everything" claims something
 * about the result that stops being true as soon as another filter applies.
 */
const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(({ className, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn(
      // `bg-input` unchecked rather than a lighter primary: an off switch is a
      // neutral surface, not a faded version of on, and the two states have to
      // be told apart at a glance in both themes.
      "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
      className
    )}
    {...props}
  >
    <SwitchPrimitive.Thumb
      className={cn(
        "pointer-events-none block size-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0",
        // The thumb travel is the whole signal, so a reader who has turned
        // animation off still needs the end position — only the motion goes.
        "motion-reduce:transition-none"
      )}
    />
  </SwitchPrimitive.Root>
));
Switch.displayName = SwitchPrimitive.Root.displayName;

export { Switch };
export type { SwitchProps };
